import React from "react";
import classNames from "classnames";
import { DebounceInput } from "react-debounce-input";
import { DropDown } from "~/common/DropDown";
import { SearchAutocompleteResults } from "./SearchAutocompleteResults";
import { useSearchQuery } from "~/hooks/useSearchQuery";
import { SearchSubmitButton } from "./SearchSubmitButton";
import { SearchCloseButton } from "./SearchCloseButton";

interface SearchAutocompleteProps {
  label?: string;
  open?: boolean;
  search_url: string;
  menuParentWidth: boolean;
  zIndex: number;
  menuGapLeft: number;
  menuGapRight: number;
}

export function SearchAutocomplete(props: SearchAutocompleteProps) {
  // Set state variables
  const [open, setOpen] = React.useState(props.open || false);
  const searchBarRef = React.useRef<HTMLDivElement>(null);
  const [inDropDown, setInDropDown] = React.useState(false);
  const [label, setLabel] = React.useState(
    props.label || "Search by Charity or Cause",
  );
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedTag, setSelectedTag] = React.useState<number>(0);

  const { loading, hasError, searchData } = useSearchQuery(
    props.search_url,
    searchTerm,
  );

  // On selected tag change
  React.useEffect(() => {
    const tags = document.querySelectorAll(
      ".autocomplete-tag, .autocomplete-custom-search",
    );

    tags.forEach((el, i) => {
      el.classList.remove(
        "autocomplete-selected",
        "tw:!bg-gray-100",
        "tw:!ring-blue-600",
      );
      if (i + 1 === selectedTag) {
        el.classList.add(
          "autocomplete-selected",
          tags[i].classList.contains("autocomplete-tag")
            ? "tw:!bg-gray-100"
            : "none",
          "tw:!ring-blue-600",
        );
        el.scrollIntoView({
          behavior: "smooth",
        });
      }
    });
  }, [selectedTag]);

  // On open props change
  React.useEffect(() => {
    setInDropDown(false);
    setOpen(props.open || false);
  }, [props.open]);

  // On open change
  React.useEffect(() => {
    if (!open) {
      setSelectedTag(0);
    }
  }, [open]);

  // On label change
  React.useEffect(() => {
    setLabel(props.label || "Search by Charity or Cause");
  }, [props.label]);

  // On search term change
  React.useEffect(() => {
    setSelectedTag(0);
  }, [searchTerm]);

  // Clear search
  const clearSearch = (e: any) => {
    e.preventDefault();
    setSearchTerm("");
  };

  // Handle key events
  const keyDownHandler = (e: any) => {
    const tags = document.querySelectorAll(
      ".autocomplete-tag, .autocomplete-custom-search",
    );

    switch (e.key) {
      case "ArrowUp": {
        setSelectedTag(Math.max(0, selectedTag - 1));
        break;
      }
      case "ArrowDown": {
        setOpen(true);
        setSelectedTag(Math.min(tags.length, selectedTag + 1));
        break;
      }
      case "ArrowLeft": {
        if (selectedTag > 0) {
          setSelectedTag(Math.max(0, selectedTag - 1));
        }
        break;
      }
      case "ArrowRight": {
        setOpen(true);
        if (selectedTag > 0) {
          setSelectedTag(Math.min(tags.length, selectedTag + 1));
        }
        break;
      }
      case "Tab": {
        break;
      }
      case "Enter": {
        if (selectedTag > 0) {
          e.preventDefault();
          (tags[selectedTag - 1] as any).click();
        }
        break;
      }
      case "Space": {
        break;
      }
      case "Escape": {
        setOpen(false);
        setInDropDown(false);
        setSelectedTag(0);
        break;
      }
      case "Home": {
        setSelectedTag(1);
        break;
      }
      case "End": {
        setSelectedTag(tags.length);
        break;
      }
    }
  };

  return (
    <div className="tw:relative tw:w-full">
      <div data-testid="SearchAutocomplete" ref={searchBarRef}>
        <form
          action={`/search?q=${searchTerm}`}
          className="tw:flex tw:font-sofia-pro"
          autoComplete="off"
        >
          <label htmlFor="search" className="tw:!hidden">
            {label}
          </label>
          <div className="tw:!relative tw:!flex tw:!flex-grow">
            <DebounceInput
              type="text"
              name="q"
              id="search"
              placeholder={label}
              className={classNames(
                "form-input tw:!flex-auto tw:!flex-grow tw:!inline-block tw:!items-stretch",
                "tw:!pl-5 tw:!pr-0 tw:!py-0 tw:!ring-0 tw:!shadow-none tw:!transition-colors",
                "tw:sm:text-sm tw:!text-base tw:!text-night-sky-800 tw:!bg-white",
                "tw:!rounded-md tw:!rounded-tr-none tw:!rounded-br-none",
                "tw:!border-opacity-100 tw:!border-gray-500 tw:!border",
                "tw:hover:!bg-gray-100 tw:hover:!border-gray-500 tw:hover:!border-opacity-50",
                "tw:!outline-none tw:focus:!ring-0 tw:focus:!bg-gray-100 tw:focus:!border-blue-400",
                "tw:placeholder:!text-gray-600",
              )}
              debounceTimeout={300}
              minLength={2}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={() => setOpen(true)}
              onFocus={() => setOpen(true)}
              onBlur={(e: any) => {
                if (open) {
                  // if the input loses focus and the user is not in the dropdown, close the menu
                  // if the user is in the dropdown, don't remove focus from the input

                  if (!inDropDown) {
                    setOpen(false);
                    setInDropDown(false); // Reset inDropDown to false after closing the menu
                  } else {
                    setTimeout(() => {
                      e.target.focus();
                    }, 0);
                  }
                }
              }}
              onKeyDown={(e) => keyDownHandler(e)}
            />
            {searchTerm !== "" && <SearchCloseButton onClick={clearSearch} />}
          </div>

          <SearchSubmitButton />
        </form>
      </div>

      {open && (
        <DropDown
          refParent={searchBarRef}
          gapTop={0}
          gapLeft={props.menuGapLeft}
          gapRight={props.menuGapRight}
          zIndex={props.zIndex}
          center={props.menuParentWidth ? "parent" : "window"}
          onMouseLeave={() => setInDropDown(false)}
          onMouseEnter={() => setInDropDown(true)}
        >
          <SearchAutocompleteResults
            loading={loading}
            hasError={hasError}
            searchData={searchData}
            searchTerm={searchTerm}
          />
        </DropDown>
      )}
    </div>
  );
}
